import React, { useState, useEffect } from "react";
import apiClient from "../../../api/axiosConfig";
import "../../../css/HelpdeskQueryList.css";
import "./QueryModal.css"; // new CSS for polished UI
import QueryResponseModal from "./QueryResponseModal";
import { ITicket, TicketStatus } from "./HelpdeskQueryList";
import { showToast } from '../../Shared/Toaster';


interface QueryModalProps {
  ticket: ITicket | null;
  onClose: () => void;
  onUpdate: () => void;
}

const QueryModal: React.FC<QueryModalProps> = ({ ticket, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableStatus, setEditableStatus] = useState<TicketStatus>("Received");
  const [showResponse, setShowResponse] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [recentMessages, setRecentMessages] = useState<any[]>([]);
  const [loadingMessages, setLoadingMessages] = useState<boolean>(false);

  useEffect(() => {
    if (ticket) {
      setEditableStatus(ticket.status);
      setIsEditing(false);

      // Fetch last 2 messages of the ticket
      fetchLastMessages();
    }
  }, [ticket?._id]);

  const fetchLastMessages = async () => {
    if (!ticket) return;
    setLoadingMessages(true);
    try {
      const res = await apiClient.helpdeskApi.get(
        `/api/admin/tickets/${ticket._id}/messages`
      );
      let list: any[] = res.data || [];

      if (ticket.message) {
        list = [
          {
            senderId: ticket.raisedBy || "user",
            messageText: ticket.message,
            sentOn: ticket.createdAt,
          },
          ...list,
        ];
      }

      // Extract the last 2 messages
      const lastTwo = list.slice(-2);
      setRecentMessages(lastTwo);
    } catch (err) {
      console.error("Error fetching messages for query modal:", err);
      if (ticket?.message) {
        setRecentMessages([
          {
            senderId: ticket.raisedBy || "user",
            messageText: ticket.message,
            sentOn: ticket.createdAt,
          },
        ]);
      } else {
        setRecentMessages([]);
      }
    } finally {
      setLoadingMessages(false);
    }
  };

  if (!ticket) return null;

  const handleUpdateClick = async () => {
  setIsUpdating(true);
  try {
    await apiClient.helpdeskApi.put(`/api/admin/tickets/${ticket._id}`, { status: editableStatus });

    // 🔹 refresh tickets in parent
    onUpdate();

    // 🔹 close modal and return to QueryList
    onClose();
  } catch (err) {
    console.error("Failed to update ticket:", err);
    showToast("Could not update status. Please try again.", 'error');
  } finally {
    setIsUpdating(false);
  }
};


  const formatDate = (dateStr?: string) => {
    if (!dateStr || dateStr === "N/A") return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="query-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="query-modal-title">
            {isEditing ? "Edit Query" : "View Query"}
          </h2>

          <div className="query-modal-grid">
            {/* Row 1 */}
            <div>
              <label>Raised By</label>
              <p className="value strong">{ticket.parentName || "—"}</p>
            </div>
            <div>
              <label>Child Name</label>
              <p className="value strong">{ticket.childName || "—"}</p>
            </div>

            {/* Row 2 */}
            <div>
              <label>Center</label>
              <p className="value strong">
                {ticket.centerName || (ticket as any).center || (ticket as any).venueName || ticket.issueType || "—"}
              </p>
            </div>
            <div>
              <label>Amount Paid</label>
              <p className="value strong">
                {(ticket as any).amountPaid ?? (ticket as any).amount ?? (ticket as any).finalAmount ?? "—"}
              </p>
            </div>

            {/* Row 3 */}
            <div>
              <label>Payment Date</label>
              <p className="value strong">
                {formatDate((ticket as any).paymentDate || ticket.createdAt)}
              </p>
            </div>
            <div>
              <label>Screening Date</label>
              <p className="value strong">
                {formatDate(ticket.appointmentDate || (ticket as any).screeningDate)}
              </p>
            </div>

            {/* Row 4 */}
            <div>
              <label>Issue Type</label>
              <p className="value strong">{ticket.issueType || "—"}</p>
            </div>
            <div>
              <label>Status</label>
              {isEditing ? (
                <select
                  className="status-dropdown"
                  value={editableStatus}
                  onChange={(e) => setEditableStatus(e.target.value as TicketStatus)}
                >
                  <option value="Open">Open</option>
                  <option value="In-progress">In-progress</option>
                  <option value="Closed">Closed</option>
                </select>
              ) : (
                <p className="value strong">{ticket.status}</p>
              )}
            </div>

            {/* Row 5 */}
            <div>
              <label>Raised On</label>
              <p className="value strong">{formatDate(ticket.createdAt)}</p>
            </div>
            <div>
              <label>Last Change</label>
              <p className="value strong">{formatDate(ticket.updatedAt)}</p>
            </div>

            {/* Row 6 */}
            <div>
              <label>Ticket Age (Days)</label>
              <p className="value strong">{ticket.ageing ?? 0}</p>
            </div>
            <div>
              <label>Status Age (Days)</label>
              <p className="value strong">{(ticket as any).statusAgeing ?? ticket.ageing ?? 0}</p>
            </div>
          </div>

          <div className="query-message">
            <label>Query Message</label>
            <div className="message-box">
              {loadingMessages ? (
                <span className="msg-loading-text">Loading messages...</span>
              ) : recentMessages.length > 0 ? (
                <div className="recent-messages-list">
                  {recentMessages.map((msg, i) => (
                    <div key={i} className="recent-msg-row">
                      <strong className="msg-sender">
                        {msg.senderId === "admin" ? "Admin" : ticket.parentName || "User"}:
                      </strong>{" "}
                      <span className="msg-content">{msg.messageText}</span>
                    </div>
                  ))}
                </div>
              ) : (
                ticket.message || "No message."
              )}
            </div>
          </div>

          <div className="query-modal-actions">
            {isEditing ? (
              <>
                <button className="btn-secondary" onClick={() => setIsEditing(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleUpdateClick} disabled={isUpdating}>
                  {isUpdating ? "Updating..." : "Update"}
                </button>
              </>
            ) : (
              <>
                <button className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
                <button className="btn-dark" onClick={() => setShowResponse(true)}>
                  Respond
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showResponse && (
        <QueryResponseModal
          ticket={ticket}
          onClose={() => setShowResponse(false)}
          onUpdate={onUpdate}
          onEditStatus={() => {
            setShowResponse(false);
            setIsEditing(true);
          }}
        />
      )}
    </>
  );
};

export default QueryModal;
